// src/pages/FulfillmentPage/index.tsx
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

import ShipOrderDialog from "../../components/fulfillment/ShipOrderDialog";
import BuyLabelDialog from "../../components/fulfillment/BuyLabelDialog";

import { useFulfillment } from "./hooks/useFulfillment";
import { useDrivers } from "./hooks/useDrivers";
import { useFulfillmentActions } from "./hooks/useFulfillmentActions";
import { extractAddress } from "./utils/helpers";

import { FulfillmentHeader } from "./components/FulfillmentHeader";
import MarkDeliveredDialog from "./components/MarkDeliveredDialog";
import { FulfillmentItemsList } from "./components/FulfillmentItemsList";
import { FulfillmentTimeline } from "./components/FulfillmentTimeline";
import { AddressCard } from "./components/AddressCard";
import { DetailsCard } from "./components/DetailsCard";
import { InfoCard } from "./components/InfoCard";
import { refundShippingLabel, updatePackingNotes } from "../../api/fulfillmentApi";

export default function FulfillmentPage() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const fulfillmentId = Number(id);
  const [shipOpen, setShipOpen] = useState(false);
  const [buyLabelOpen, setBuyLabelOpen] = useState(false);
  
  // Accordion state - using an object to track multiple expanded accordions
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({
    address: true, // Default expanded
    details: true, // Default expanded
    info: true,    // Default expanded
  });

  const {
    loading,
    error,
    fulfillment,
    latestLabel,
labelHistory,
reload,
    setError,
  } = useFulfillment(fulfillmentId);

  const {
    drivers,
    driverLoading,
    selectedDriverId,
    setSelectedDriverId,
    assignDriver,
  } = useDrivers(fulfillment);

  const {
    actionLoading,
    actionError,
    setActionError,
    handleAction,
    handleStart,
    handleMarkReady,
    handleComplete,
    handleMarkDelivered,
  } = useFulfillmentActions(fulfillment, reload);

  const [deliveredDialogOpen, setDeliveredDialogOpen] = useState(false);

  const address = useMemo(() => extractAddress(fulfillment), [fulfillment]);

  const isLocalDelivery = useMemo(() => {
    if (!fulfillment) return false;
    if (String(fulfillment.type).toLowerCase() === "local_delivery") return true;
    return Number(fulfillment.delivery_fee_cents || 0) > 0;
  }, [fulfillment]);

  const isShipping = useMemo(() => {
    if (!fulfillment) return false;
    return String(fulfillment.type).toLowerCase() !== "local_delivery";
  }, [fulfillment]);

  const hasLabelRecord = !!latestLabel;
  const hasLabelUrl = !!latestLabel?.label_url;

  const canBuyLabel = !!fulfillment && isShipping && fulfillment.status === "READY" && !hasLabelRecord;

  const canStart = fulfillment?.status === "NEW";
  const canMarkReady = fulfillment?.status === "PACKING";
  const canComplete = fulfillment?.status === "READY" && (!isLocalDelivery || !!fulfillment.delivery_driver_id);
  const canMarkDelivered = String(fulfillment?.type).toLowerCase() === "local_delivery" && fulfillment?.status === "OUT_FOR_DELIVERY";

  const handleCompleteClick = () => {
    if (!fulfillment) return;
    if (!isShipping) {
      handleComplete();
    } else {
      setShipOpen(true);
    }
  };

  const submitShip = (payload: any) => {
    handleComplete(payload);
    setShipOpen(false);
  };

  const handleBuyLabel = () => {
    if (!fulfillment) return;
    setBuyLabelOpen(true);
  };

  const handleBuyLabelSuccess = async () => {
    await reload();
  };

  const handleRefundLabel = async () => {
  if (!latestLabel?.id) return;

  handleAction(async () => {
    await refundShippingLabel(latestLabel.id);
  });
};

  const handleAssignDriver = () => {
    if (!fulfillment || !selectedDriverId) return;
    handleAction(async () => {
      await assignDriver(fulfillment.id, Number(selectedDriverId));
    });
  };

  const handleUpdateNotes = async (notes: string) => {
    if (!fulfillment) return;
    handleAction(async () => {
      await updatePackingNotes(fulfillment.id, notes);
    });
  };

  // Toggle accordion without closing others
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Optional: Add buttons to expand/collapse all
  const expandAll = () => {
    setExpandedAccordions({
      address: true,
      details: true,
      info: true,
    });
  };

  const collapseAll = () => {
    setExpandedAccordions({
      address: false,
      details: false,
      info: false,
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", overflow: "hidden", mt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading fulfillment…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", overflow: "hidden", mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={reload}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!fulfillment) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", overflow: "hidden", mt: 2 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Fulfillment not found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      mt: 1
    }}>
      <FulfillmentHeader
        fulfillment={fulfillment}
        latestLabel={latestLabel}
        labelHistory={labelHistory}
        actionLoading={actionLoading}
        isLocalDelivery={isLocalDelivery}
        isShipping={isShipping}
        hasLabelRecord={hasLabelRecord}
        hasLabelUrl={hasLabelUrl}
        canStart={canStart}
        canMarkReady={canMarkReady}
        canBuyLabel={canBuyLabel}
        canComplete={canComplete}
        canMarkDelivered={canMarkDelivered}
        onStart={handleStart}
        onMarkReady={handleMarkReady}
        onBuyLabel={handleBuyLabel}
        onRefundLabel={handleRefundLabel}
        onComplete={handleCompleteClick}
        onMarkDelivered={() => setDeliveredDialogOpen(true)}
      />

      <MarkDeliveredDialog
        open={deliveredDialogOpen}
        loading={actionLoading}
        onClose={() => setDeliveredDialogOpen(false)}
        onConfirm={(msg) => {
          setDeliveredDialogOpen(false);
          handleMarkDelivered(msg);
        }}
      />

      {(error || actionError) && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error || actionError}
        </Alert>
      )}

      {/* Optional: Add expand/collapse controls */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        <Button size="small" onClick={expandAll} variant="outlined">
          Expand All
        </Button>
        <Button size="small" onClick={collapseAll} variant="outlined">
          Collapse All
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <FulfillmentItemsList items={fulfillment.items} />
            <FulfillmentTimeline events={fulfillment.events} />
          </Stack>
        </Grid>

        {/* Right Column - Accordions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* Address Accordion */}
            <Accordion 
              expanded={expandedAccordions.address} 
              onChange={handleAccordionChange('address')}
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: theme.shadows[1],
                border: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnIcon color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {isLocalDelivery ? "Delivery Address" : "Shipping Address"}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <AddressCard
                  address={address}
                  fulfillment={fulfillment}
                  isLocalDelivery={isLocalDelivery}
                />
              </AccordionDetails>
            </Accordion>

            {/* Details Accordion */}
            <Accordion 
              expanded={expandedAccordions.details} 
              onChange={handleAccordionChange('details')}
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: theme.shadows[1],
                border: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssignmentIndIcon color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Fulfillment Details
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <DetailsCard
                  fulfillment={fulfillment}
                  isLocalDelivery={isLocalDelivery}
                  drivers={drivers}
                  driverLoading={driverLoading}
                  actionLoading={actionLoading}
                  selectedDriverId={selectedDriverId}
                  onDriverChange={setSelectedDriverId}
                  onAssignDriver={handleAssignDriver}
                  onUpdateNotes={handleUpdateNotes}
                />
              </AccordionDetails>
            </Accordion>

            {/* Info Accordion */}
            <Accordion 
              expanded={expandedAccordions.info} 
              onChange={handleAccordionChange('info')}
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: theme.shadows[1],
                border: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InfoIcon color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Order Info
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <InfoCard fulfillment={fulfillment} />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Grid>
      </Grid>

      <ShipOrderDialog
        open={shipOpen}
        loading={actionLoading}
        onClose={() => setShipOpen(false)}
        onSubmit={submitShip}
        existingTracking={{
          carrier: fulfillment.carrier || null,
          tracking_number: fulfillment.tracking_number || null,
          tracking_url: fulfillment.tracking_url || null,
        }}
      />

      <BuyLabelDialog
        open={buyLabelOpen}
        loading={actionLoading}
        onClose={() => setBuyLabelOpen(false)}
        fulfillmentId={fulfillment.id}
        orderId={fulfillment.order_id}
        onPurchased={handleBuyLabelSuccess}
        hasExistingLabel={hasLabelRecord}
      />
    </Box>
  );
}